// Layout of the horizontal media strip under a comment.
//
// A comment can have a recording attached (`connectedRecording`), and that recording can have a
// finished 3D flyover video. When it does, the strip leads with a playable video tile — the same
// treatment the recording overview page gives its gallery — followed by the comment's photos.
//
// The index bookkeeping is the fiddly part: the leading video tile shifts every photo one slot to
// the right, so the "View more" button's jump target is no longer simply `previewCount`. Kept here
// as pure functions so it is unit-tested rather than inlined in CommentPhotos.vue.

/** How many tiles (video included) the strip shows before collapsing into "View more". */
export const MAX_PREVIEW_TILES = 4;

export interface CommentPhoto {
  id: string | number;
  file?: { lgUrl?: string; mdUrl?: string; smUrl?: string };
}

export interface CommentLike {
  images?: CommentPhoto[] | null;
  connectedRecording?: { readyFlyoverVideoUrl?: string | null } | null;
}

export interface FlyoverTile {
  kind: 'flyover';
  videoUrl: string;
}

export interface PhotoTile {
  kind: 'photo';
  photo: CommentPhoto;
  /** Index into the comment's own `images` array — what the lightbox opens at. */
  photoIndex: number;
}

export type CommentMediaTile = FlyoverTile | PhotoTile;

/**
 * Playable URL of the attached recording's finished flyover video, or null. Whitespace-only and
 * empty strings count as absent: the backend returns null once a render is ready, but a blank
 * string would otherwise render a tile that plays nothing.
 */
export function commentFlyoverVideoUrl(comment: CommentLike | null | undefined): string | null {
  const url = comment?.connectedRecording?.readyFlyoverVideoUrl;
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Every tile the strip could show, flyover first. */
export function commentMediaTiles(comment: CommentLike | null | undefined): CommentMediaTile[] {
  const tiles: CommentMediaTile[] = [];

  const videoUrl = commentFlyoverVideoUrl(comment);
  if (videoUrl) tiles.push({ kind: 'flyover', videoUrl });

  (comment?.images ?? []).forEach((photo, photoIndex) => {
    tiles.push({ kind: 'photo', photo, photoIndex });
  });

  return tiles;
}

/** The tiles actually rendered, capped at `maxTiles`. */
export function previewMediaTiles(
  comment: CommentLike | null | undefined,
  maxTiles: number = MAX_PREVIEW_TILES,
): CommentMediaTile[] {
  return commentMediaTiles(comment).slice(0, Math.max(0, maxTiles));
}

/** True when at least one tile didn't fit and the "View more" button is needed. */
export function hasHiddenMediaTiles(
  comment: CommentLike | null | undefined,
  maxTiles: number = MAX_PREVIEW_TILES,
): boolean {
  return commentMediaTiles(comment).length > Math.max(0, maxTiles);
}

/**
 * Index into the comment's `images` of the first photo the strip had to hide — where "View more"
 * opens the lightbox. Returns null when nothing is hidden, or when the only hidden tiles somehow
 * aren't photos. A leading flyover tile consumes a preview slot, so this is one lower than the
 * photo-only case.
 */
export function firstHiddenPhotoIndex(
  comment: CommentLike | null | undefined,
  maxTiles: number = MAX_PREVIEW_TILES,
): number | null {
  const hidden = commentMediaTiles(comment).slice(Math.max(0, maxTiles));
  const firstPhoto = hidden.find((tile): tile is PhotoTile => tile.kind === 'photo');
  return firstPhoto ? firstPhoto.photoIndex : null;
}
